import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { buildApiUrl } from '../../config/apiConfig';
import { scrollableTabsSx } from '../../constants/appShellLayout';

const TICKET_CATEGORIES = [
  { value: 'account_access', label: 'Account access' },
  { value: 'billing', label: 'Billing' },
  { value: 'data_privacy', label: 'Data privacy' },
  { value: 'technical', label: 'Technical' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'other', label: 'Other' },
] as const;

const TICKET_PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
] as const;

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_on_customer: 'Waiting on you',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  open: 'info',
  in_progress: 'warning',
  waiting_on_customer: 'warning',
  resolved: 'success',
  closed: 'default',
};

export interface MsmeSupportTicket {
  _id: string;
  subject: string;
  description?: string;
  category?: string;
  status: string;
  priority?: string;
  solution?: string;
  aiDraftAnswer?: string;
  createdAt?: string;
  updatedAt?: string;
  resolvedAt?: string | null;
  csatRequested?: boolean;
  csatSubmittedAt?: string | null;
}

interface MsmeSupportPanelProps {
  isRegistered: boolean;
}

export const MsmeSupportPanel: React.FC<MsmeSupportPanelProps> = ({ isRegistered }) => {
  const [tab, setTab] = useState(0);

  const [question, setQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState<string | null>(null);
  const [resolutionSteps, setResolutionSteps] = useState<string[]>([]);
  const [aiConfidence, setAiConfidence] = useState<number | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [escalated, setEscalated] = useState(false);
  const [escalateTicketId, setEscalateTicketId] = useState<string | null>(null);
  const [escalating, setEscalating] = useState(false);

  const [tickets, setTickets] = useState<MsmeSupportTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<MsmeSupportTicket | null>(null);

  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketCategory, setTicketCategory] = useState<string>('other');
  const [ticketPriority, setTicketPriority] = useState<string>('medium');
  const [ticketCreating, setTicketCreating] = useState(false);
  const [ticketCreateSuccess, setTicketCreateSuccess] = useState<string | null>(null);

  const [csatDrafts, setCsatDrafts] = useState<Record<string, { rating: number; comment: string }>>({});
  const [csatSubmitting, setCsatSubmitting] = useState<string | null>(null);
  const [csatSuccess, setCsatSuccess] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  const fetchTickets = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) {
      setTickets([]);
      return;
    }
    setTicketsLoading(true);
    try {
      const response = await axios.get(buildApiUrl('/support-queries/my-tickets'), { headers });
      setTickets(response.data.data?.tickets || []);
    } catch {
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isRegistered) {
      fetchTickets();
    }
  }, [isRegistered, fetchTickets]);

  const pendingCsatTickets = tickets.filter(
    (ticket) =>
      ['resolved', 'closed'].includes(ticket.status) &&
      ticket.csatRequested &&
      !ticket.csatSubmittedAt
  );

  const handleAskQuestion = async () => {
    if (!question.trim()) return;
    const headers = getAuthHeaders();
    if (!headers) {
      setAiError('Sign in to your MSME account to ask a question and receive an AI-assisted answer.');
      return;
    }

    setAiLoading(true);
    setAiError(null);
    setAiAnswer(null);
    setResolutionSteps([]);
    setEscalated(false);
    setEscalateTicketId(null);
    setAiConfidence(null);

    try {
      const response = await axios.post(
        buildApiUrl('/support-queries/ask'),
        { message: question.trim(), subject: question.trim().slice(0, 80) },
        { headers }
      );
      const data = response.data.data;
      setAiAnswer(data.answer);
      setResolutionSteps(data.resolutionSteps || []);
      setAiConfidence(data.confidence ?? null);
      // BUG-086: Ask never auto-creates a ticket. Manual escalate remains available.
      setEscalated(false);
      setEscalateTicketId(null);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAiError(message || 'Could not process your question. Email contact@sustainow.in for help.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleManualEscalate = async () => {
    if (!question.trim()) return;
    const headers = getAuthHeaders();
    if (!headers) return;

    setEscalating(true);
    setAiError(null);
    try {
      const response = await axios.post(
        buildApiUrl('/support-queries/escalate'),
        {
          message: question.trim(),
          subject: question.trim().slice(0, 80),
          aiAnswer: aiAnswer || undefined,
          aiConfidence: aiConfidence ?? undefined,
        },
        { headers }
      );
      setEscalated(true);
      setEscalateTicketId(response.data.data?.ticketId || null);
      if (response.data.data?.resolutionSteps?.length) {
        setResolutionSteps(response.data.data.resolutionSteps);
      }
      await fetchTickets();
      setTab(1);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAiError(message || 'Could not escalate to support. Please try again.');
    } finally {
      setEscalating(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!ticketSubject.trim() || !ticketDescription.trim()) return;
    const headers = getAuthHeaders();
    if (!headers) {
      setAiError('Sign in to create a support ticket.');
      return;
    }

    setTicketCreating(true);
    setAiError(null);
    setTicketCreateSuccess(null);
    try {
      const response = await axios.post(
        buildApiUrl('/support-queries/tickets'),
        {
          subject: ticketSubject.trim(),
          description: ticketDescription.trim(),
          category: ticketCategory,
          priority: ticketPriority,
        },
        { headers }
      );
      const created = response.data.data?.ticket;
      setTicketCreateSuccess(created?._id || 'created');
      setTicketSubject('');
      setTicketDescription('');
      setTicketCategory('other');
      setTicketPriority('medium');
      await fetchTickets();
      setTab(1);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAiError(message || 'Could not create ticket. Please try again.');
    } finally {
      setTicketCreating(false);
    }
  };

  const handleSubmitCsat = async (ticketId: string) => {
    const draft = csatDrafts[ticketId];
    if (!draft?.rating) return;
    const headers = getAuthHeaders();
    if (!headers) return;

    setCsatSubmitting(ticketId);
    setCsatSuccess(null);
    try {
      await axios.post(
        buildApiUrl(`/support-queries/tickets/${ticketId}/csat`),
        { rating: draft.rating, comment: draft.comment || undefined },
        { headers }
      );
      setCsatSuccess(ticketId);
      await fetchTickets();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAiError(message || 'Could not submit feedback. Please try again.');
    } finally {
      setCsatSubmitting(null);
    }
  };

  const handleViewTicket = async (ticketId: string) => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(buildApiUrl(`/support-queries/tickets/${ticketId}`), { headers });
      setSelectedTicket(response.data.data?.ticket || null);
    } catch {
      setSelectedTicket(tickets.find((t) => t._id === ticketId) || null);
    }
  };

  if (!isRegistered) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Support
        </Typography>
        <Alert severity="info">
          Sign in and complete MSME registration to ask questions, create tickets, and track support requests.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Support
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Get instant AI-assisted answers with step-by-step guidance, create tickets, or escalate to our support team.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, ...scrollableTabsSx }}
      >
        <Tab label="Ask AI" />
        <Tab label={`My tickets (${tickets.length})`} />
        <Tab label="Create ticket" />
      </Tabs>

      {aiError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAiError(null)}>
          {aiError}
        </Alert>
      )}

      {tab === 0 && (
        <Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Your question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleAskQuestion}
              disabled={aiLoading || !question.trim()}
            >
              {aiLoading ? <CircularProgress size={22} color="inherit" /> : 'Get AI answer'}
            </Button>
            {aiAnswer && !escalated && !escalateTicketId && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleManualEscalate}
                disabled={escalating || !question.trim()}
              >
                {escalating ? 'Escalating…' : 'Escalate to support team'}
              </Button>
            )}
          </Stack>

          {aiAnswer && (
            <Alert severity={escalated || escalateTicketId ? 'info' : 'success'} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {aiAnswer}
              </Typography>
              {(escalated || escalateTicketId) && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {escalateTicketId
                    ? `Ticket #${escalateTicketId.slice(-6)} created — our support team will follow up.`
                    : 'Your query was escalated to the support team for human review.'}
                </Typography>
              )}
            </Alert>
          )}

          {resolutionSteps.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Steps to resolve
              </Typography>
              <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
                {resolutionSteps.map((step, index) => (
                  <Typography component="li" variant="body2" key={`step-${index}`}>
                    {step}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {ticketsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : tickets.length === 0 ? (
            <Alert severity="info">You have no support tickets yet. Ask a question or create a ticket to get started.</Alert>
          ) : (
            <Stack spacing={1.5}>
              {tickets.map((ticket) => (
                <Paper key={ticket._id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">{ticket.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString('en-IN') : ''}
                        {ticket.category ? ` · ${ticket.category.replace(/_/g, ' ')}` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[ticket.status] || ticket.status}
                      color={STATUS_COLORS[ticket.status] || 'default'}
                    />
                  </Stack>
                  {ticket.solution && ['resolved', 'closed'].includes(ticket.status) && (
                    <Alert severity="success" sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        <strong>Resolution:</strong> {ticket.solution}
                      </Typography>
                    </Alert>
                  )}
                  {!ticket.solution && ticket.aiDraftAnswer && !['resolved', 'closed'].includes(ticket.status) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      AI suggestion pending human review.
                    </Typography>
                  )}
                  <Button size="small" sx={{ mt: 1 }} onClick={() => handleViewTicket(ticket._id)}>
                    View details
                  </Button>
                </Paper>
              ))}
            </Stack>
          )}

          {selectedTicket && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">{selectedTicket.subject}</Typography>
                <Button size="small" onClick={() => setSelectedTicket(null)}>Close</Button>
              </Stack>
              <Chip
                size="small"
                label={STATUS_LABELS[selectedTicket.status] || selectedTicket.status}
                color={STATUS_COLORS[selectedTicket.status] || 'default'}
                sx={{ mb: 1.5 }}
              />
              {selectedTicket.description && (
                <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
                  {selectedTicket.description}
                </Typography>
              )}
              {selectedTicket.solution && (
                <Alert severity="success">
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.solution}
                  </Typography>
                </Alert>
              )}
              {!selectedTicket.solution && selectedTicket.aiDraftAnswer && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.aiDraftAnswer}
                  </Typography>
                </Alert>
              )}
            </Paper>
          )}

          {pendingCsatTickets.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Rate your recent support experience
              </Typography>
              <Stack spacing={2}>
                {pendingCsatTickets.map((ticket) => (
                  <Paper key={`csat-${ticket._id}`} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{ticket.subject}</Typography>
                    {csatSuccess === ticket._id ? (
                      <Alert severity="success">Thank you for your feedback.</Alert>
                    ) : (
                      <>
                        <Rating
                          value={csatDrafts[ticket._id]?.rating || 0}
                          onChange={(_, value) => {
                            setCsatDrafts((prev) => ({
                              ...prev,
                              [ticket._id]: {
                                rating: value || 0,
                                comment: prev[ticket._id]?.comment || '',
                              },
                            }));
                          }}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Optional comment"
                          value={csatDrafts[ticket._id]?.comment || ''}
                          onChange={(e) => {
                            setCsatDrafts((prev) => ({
                              ...prev,
                              [ticket._id]: {
                                rating: prev[ticket._id]?.rating || 0,
                                comment: e.target.value,
                              },
                            }));
                          }}
                          sx={{ mb: 1.5 }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={!csatDrafts[ticket._id]?.rating || csatSubmitting === ticket._id}
                          onClick={() => handleSubmitCsat(ticket._id)}
                        >
                          {csatSubmitting === ticket._id ? 'Submitting…' : 'Submit feedback'}
                        </Button>
                      </>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          {ticketCreateSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Support ticket created. Our team will respond shortly. View it under My tickets.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Subject"
            value={ticketSubject}
            onChange={(e) => setTicketSubject(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel id="ticket-category-label">Category</InputLabel>
              <Select
                labelId="ticket-category-label"
                label="Category"
                value={ticketCategory}
                onChange={(e) => setTicketCategory(e.target.value)}
              >
                {TICKET_CATEGORIES.map((item) => (
                  <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="ticket-priority-label">Priority</InputLabel>
              <Select
                labelId="ticket-priority-label"
                label="Priority"
                value={ticketPriority}
                onChange={(e) => setTicketPriority(e.target.value)}
              >
                {TICKET_PRIORITIES.map((item) => (
                  <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Describe your issue"
            value={ticketDescription}
            onChange={(e) => setTicketDescription(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Button
            variant="contained"
            onClick={handleCreateTicket}
            disabled={ticketCreating || !ticketSubject.trim() || !ticketDescription.trim()}
          >
            {ticketCreating ? <CircularProgress size={22} color="inherit" /> : 'Submit ticket'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
